import { FC, useCallback, useEffect, useState } from 'react';
import styles from './ReserveModal.module.css';
import { Modal } from '../Modal';
import { FormattedMessage } from 'react-intl';
import {
  reserveVehicle,
  useAvailableVehicles,
  useDateRangeValidation,
  useReserveVehicle,
} from '../../services/reservations';
import { PlaceCenter } from '../PlaceCenter';
import { Spinner } from '../Spinner';
import { CarCard } from '../CarCard';
import {
  CAN_EV_DISTANCE_UNTIL_RECHARGE,
  CAN_EV_STATE_OF_CHARGE,
  FUEL_LEVEL,
  OBD_EV_DISTANCE_UNTIL_RECHARGE,
  OBD_EV_STATE_OF_CHARGE,
} from '../AssignationModal/constants';
import { Button } from '../Button';
import { DatePicker } from './DatePicker';
import clsx from 'clsx';
import { useConfirmDialog } from '../ConfirmDialogProvider';
import { noop } from 'rxjs';
import { SearchField } from '../SearchField';

interface ReserveModalProps {
  open: boolean;
  onClose: () => void;
}

export const ReserveModal: FC<ReserveModalProps> = (props) => {
  const { error: validityError } = useDateRangeValidation();
  const [query, setQuery] = useState('');

  const { result, running } = useAvailableVehicles();
  const [selected, setSelected] = useState<number | null>(null);
  const { open: confirm, close: destroy } = useConfirmDialog();

  const onReserve = useCallback(
    () =>
      confirm(
        <FormattedMessage tagName="p" id="Vehicle.Reserve.Confirm" />
      ).then(() => reserveVehicle(selected!), noop),
    [selected, confirm]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => destroy(), []);

  useReserveVehicle();

  return (
    <Modal title={<FormattedMessage id="Reserve.Vehicle" />} {...props}>
      <div className={styles.root} data-testid="reserve-dialog">
        <DatePicker />
        <SearchField
          fullWidth
          id="search-vehicle"
          onChange={(e) => setQuery(e.target.value)}
          value={query}
          autoComplete="off"
        />
        <div className={styles.list}>
          {running && (
            <PlaceCenter>
              <Spinner />
            </PlaceCenter>
          )}
          {!running && (
            <>
              {validityError ? (
                <div className={clsx(styles.error, 'light')}>
                  <FormattedMessage id={validityError} />
                </div>
              ) : (
                <>
                  {!result?.length && (
                    <span className="light">
                      <FormattedMessage id="Reserve.Vehicles.Empty" />
                    </span>
                  )}
                  {!!result?.length &&
                    result
                      .filter(
                        (car) =>
                          car.name
                            .toLowerCase()
                            .indexOf(query.toLocaleLowerCase()) > -1
                      )
                      .map((car) => (
                        <CarCard
                          key={car.id}
                          vehicleName={car.name}
                          driverName={car.firstDriver?.fullName}
                          onClick={() => setSelected(car.id)}
                          active={!!selected && selected === car.id}
                          fuelLevel={car.inputs[FUEL_LEVEL]}
                          stateOfCharge={
                            car.inputs[CAN_EV_STATE_OF_CHARGE] ??
                            car.inputs[OBD_EV_STATE_OF_CHARGE]
                          }
                          distanceUntilRecharge={
                            car.inputs[CAN_EV_DISTANCE_UNTIL_RECHARGE] ??
                            car.inputs[OBD_EV_DISTANCE_UNTIL_RECHARGE]
                          }
                        />
                      ))}
                </>
              )}
            </>
          )}
        </div>
        {!!selected && (
          <Button
            onClick={onReserve}
            className={styles.button}
            disabled={running}
          >
            <FormattedMessage id="Reserve" />
          </Button>
        )}
      </div>
    </Modal>
  );
};
